/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  if (customElements.get('artwork-atlas')) return;

  var html = document.documentElement;
  var GOLDEN_ANGLE = 2.399963229728653;
  var PAGE_SIZE = 250;
  var THUMB_PROBE = 12;
  var MAX_IMG_LOADS = 40;
  var BATCH_SIZE = 120;

  function motionOn() {
    return !!window.gsap && html.classList.contains('motion');
  }

  function prefersReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function interp(str, vars) {
    return String(str == null ? '' : str).replace(/\[(\w+)\]/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : m;
    });
  }

  function sizedImage(src, width) {
    if (!src) return '';
    var q = src.indexOf('?');
    var path = q === -1 ? src : src.slice(0, q);
    var query = q === -1 ? '' : src.slice(q);
    var dot = path.lastIndexOf('.');
    if (dot === -1) return src;
    return path.slice(0, dot) + '_' + width + 'x' + path.slice(dot) + query;
  }

  function centsFrom(price) {
    var n = parseFloat(price);
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  function formatMoney(cents) {
    if (window.AirX && window.AirX.formatMoney) return window.AirX.formatMoney(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  function hashString(str) {
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function mulberry32(a) {
    return function () {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function stripHtmlExcerpt(htmlText, maxWords) {
    if (!htmlText) return '';
    var text = '';
    try {
      text = new DOMParser().parseFromString(htmlText, 'text/html').body.textContent || '';
    } catch (err) {
      text = String(htmlText).replace(/<[^>]*>/g, ' ');
    }
    text = text.replace(/\s+/g, ' ').trim();
    if (!text) return '';
    var words = text.split(' ');
    return words.length <= maxWords ? text : words.slice(0, maxWords).join(' ') + '…';
  }

  function prettySize(value) {
    var m = /^(\d+)L"x(\d+)H"$/.exec(value || '');
    return m ? m[1] + ' × ' + m[2] + '″' : value;
  }

  function prettifyHandle(handle) {
    return String(handle || '')
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); })
      .trim();
  }

  function filterProducts(raw) {
    return (raw || []).filter(function (p) {
      return p && p.handle !== 'custom-canvas' && p.images && p.images.length > 0;
    });
  }

  class ArtworkAtlas extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      var strEl = this.querySelector('[data-atlas-strings]');
      try {
        this.strings = strEl ? JSON.parse(strEl.textContent) : {};
      } catch (err) {
        this.strings = {};
      }

      this.root = this.dataset.root || '/';
      if (this.root.slice(-1) !== '/') this.root += '/';
      this.sortMode = this.dataset.sort === 'alpha' ? 'alpha' : 'count';
      this.showStats = this.hasAttribute('data-show-stats');

      this.excluded = Object.create(null);
      (this.dataset.excluded || '').split(/[\n,]+/).forEach(function (h) {
        h = h.trim().toLowerCase();
        if (h) this.excluded[h] = true;
      }, this);

      this.state = {
        view: 'constellations',
        categories: [],
        activeCategory: null,
        products: new Map(),
        selection: -1
      };
      this.items = [];
      this.tiles = [];
      this.filterQuery = '';
      this.savedScroll = 0;
      this.runToken = 0;
      this.stageToken = 0;
      this.statsBuilt = false;
      this.catsLoaded = false;
      this.sheetOpen = false;
      this.currentDepth = null;
      this.selectedSize = null;
      this.currentVariant = null;
      this.lastCents = null;
      this.preloaded = {};
      this.loadingImages = 0;
      this.imageQueue = [];
      this.lastFocusedCardHandle = null;

      this.mqDesktop = window.matchMedia('(min-width: 990px)');

      this.grabRefs();
      this.bindEvents();
      this.buildImageObserver();
      this.buildCardObserver();

      this.onHashChange = this.route.bind(this);
      window.addEventListener('hashchange', this.onHashChange);
      this.route(true);

      this.loadCategories();
    }

    disconnectedCallback() {
      window.removeEventListener('hashchange', this.onHashChange);
      if (this.catsAbort) this.catsAbort.abort();
      if (this.wallAbort) this.wallAbort.abort();
      if (this.tileIO) this.tileIO.disconnect();
      if (this.cardIO) this.cardIO.disconnect();
      if (this.onBackdropClick && window.AirX && window.AirX.off) {
        window.AirX.off('overlay:backdrop-click', this.onBackdropClick);
      }
      if (this.onMqChange) {
        if (this.mqDesktop.removeEventListener) this.mqDesktop.removeEventListener('change', this.onMqChange);
        else if (this.mqDesktop.removeListener) this.mqDesktop.removeListener(this.onMqChange);
      }
      if (this.sheetOpen) this.collapseSheet(true);
      this._booted = false;
    }

    grabRefs() {
      this.constellationsEl = this.querySelector('[data-atlas-constellations]');
      this.gridEl = this.querySelector('[data-atlas-grid]');
      this.gridSkeleton = this.querySelector('[data-atlas-grid-skeleton]');
      this.catsErrorEl = this.querySelector('[data-atlas-cats-error]');
      this.statsEl = this.querySelector('[data-atlas-stats]');
      this.statMoodsEl = this.querySelector('[data-stat-moods]');
      this.statArtworksEl = this.querySelector('[data-stat-artworks]');

      this.explorerEl = this.querySelector('[data-atlas-explorer]');
      this.titleEl = this.querySelector('[data-explorer-title]');
      this.titleGhost = this.querySelector('[data-explorer-title-ghost]');
      this.countEl = this.querySelector('[data-explorer-count]');
      this.linkEl = this.querySelector('[data-explorer-link]');
      this.filterInput = this.querySelector('[data-atlas-filter]');

      this.wallEl = this.querySelector('[data-atlas-wall]');
      this.wallSkeleton = this.querySelector('[data-atlas-wall-skeleton]');
      this.wallErrorEl = this.querySelector('[data-atlas-wall-error]');
      this.wallEmptyEl = this.querySelector('[data-atlas-wall-empty]');
      this.filterEmptyEl = this.querySelector('[data-atlas-filter-empty]');
      this.filterEmptyTitle = this.querySelector('[data-atlas-filter-empty-title]');
      this.progressEl = this.querySelector('[data-atlas-progress]');
      this.progressBar = this.querySelector('[data-atlas-progress-bar]');
      this.statusEl = this.querySelector('[data-atlas-status]');
      this.liveEl = this.querySelector('[data-atlas-live]');

      this.previewEl = this.querySelector('[data-atlas-preview]');
      this.previewScroll = this.querySelector('[data-atlas-preview-scroll]');
      this.previewEmpty = this.querySelector('[data-preview-empty]');
      this.previewContent = this.querySelector('[data-preview-content]');
      var imgs = this.querySelectorAll('[data-preview-img]');
      this.frontImg = imgs[0];
      this.backImg = imgs[1];
      this.previewTitle = this.querySelector('[data-preview-title]');
      this.priceEl = this.querySelector('[data-preview-price]');
      this.excerptEl = this.querySelector('[data-preview-excerpt]');
      this.sizesWrap = this.querySelector('[data-preview-sizes-wrap]');
      this.sizesEl = this.querySelector('[data-preview-sizes]');
      this.depthsWrap = this.querySelector('[data-preview-depths-wrap]');
      this.depthsEl = this.querySelector('[data-preview-depths]');
      this.previewError = this.querySelector('[data-preview-error]');
      this.atcButton = this.querySelector('.atlas-preview__atc');
      this.atcLabel = this.querySelector('[data-atc-label]');
      this.detailsLink = this.querySelector('[data-preview-details]');

      this.peekEl = this.querySelector('[data-atlas-peek]');
      this.peekExpand = this.querySelector('[data-atlas-peek-expand]');
      this.peekImg = this.querySelector('[data-peek-img]');
      this.peekTitle = this.querySelector('[data-peek-title]');
      this.peekPrice = this.querySelector('[data-peek-price]');
      this.sheetHandle = this.querySelector('[data-atlas-sheet-handle]');
      this.sheetClose = this.querySelector('[data-atlas-sheet-close]');
    }

    bindEvents() {
      var self = this;

      this.querySelectorAll('[data-atlas-back]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.exitExplorer(); });
      });

      var catsRetry = this.querySelector('[data-atlas-cats-retry]');
      if (catsRetry) catsRetry.addEventListener('click', function () { self.loadCategories(); });
      var wallRetry = this.querySelector('[data-atlas-wall-retry]');
      if (wallRetry) {
        wallRetry.addEventListener('click', function () {
          if (self.state.activeCategory) self.startProducts(self.state.activeCategory);
        });
      }

      if (this.filterInput) {
        var debounced = this.debounce(function () { self.applyFilter(); }, 120);
        this.filterInput.addEventListener('input', debounced);
        this.filterInput.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && self.filterInput.value) {
            e.stopPropagation();
            self.clearFilter();
          }
        });
      }
      this.querySelectorAll('[data-atlas-filter-clear]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.clearFilter(true); });
      });

      if (this.wallEl) {
        this.wallEl.addEventListener('pointerover', function (e) {
          if (e.pointerType === 'touch') return;
          var tile = e.target.closest('.atlas-tile');
          if (!tile || tile === self._hoverTile) return;
          self._hoverTile = tile;
          clearTimeout(self._hoverTimer);
          self._hoverTimer = setTimeout(function () {
            self.selectTile(parseInt(tile.dataset.index, 10), {});
          }, 60);
        });
        this.wallEl.addEventListener('pointerout', function (e) {
          var tile = e.target.closest('.atlas-tile');
          if (!tile) return;
          if (e.relatedTarget && tile.contains(e.relatedTarget)) return;
          if (tile === self._hoverTile) self._hoverTile = null;
          clearTimeout(self._hoverTimer);
        });
        this.wallEl.addEventListener('click', function (e) {
          var tile = e.target.closest('.atlas-tile');
          if (!tile) return;
          clearTimeout(self._hoverTimer);
          self.selectTile(parseInt(tile.dataset.index, 10), { focus: true });
        });
        this.wallEl.addEventListener('focusin', function (e) {
          var tile = e.target.closest('.atlas-tile');
          if (!tile) return;
          var index = parseInt(tile.dataset.index, 10);
          if (index !== self.state.selection) self.selectTile(index, {});
        });
        this.wallEl.addEventListener('keydown', function (e) { self.onWallKeydown(e); });
      }

      this.querySelectorAll('[data-atlas-atc]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.addToCart(btn); });
      });

      if (this.peekExpand) {
        this.peekExpand.addEventListener('click', function () { self.expandSheet(); });
        this.peekExpand.addEventListener('touchstart', function (e) {
          self._peekTouchY = e.touches[0].clientY;
        }, { passive: true });
        this.peekExpand.addEventListener('touchend', function (e) {
          if (self._peekTouchY == null) return;
          var dy = e.changedTouches[0].clientY - self._peekTouchY;
          self._peekTouchY = null;
          if (dy < -24) self.expandSheet();
        }, { passive: true });
      }
      if (this.sheetClose) this.sheetClose.addEventListener('click', function () { self.collapseSheet(); });
      if (this.sheetHandle) {
        this.sheetHandle.addEventListener('click', function () { self.collapseSheet(); });
        this.bindSheetDrag();
      }

      this.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        if (self.sheetOpen) {
          e.stopPropagation();
          self.collapseSheet();
        } else if (self.state.view === 'explorer') {
          self.exitExplorer();
        }
      });

      if (window.AirX && window.AirX.on) {
        this.onBackdropClick = function () { if (self.sheetOpen) self.collapseSheet(); };
        window.AirX.on('overlay:backdrop-click', this.onBackdropClick);
      }

      this.onMqChange = function () { self.syncViewport(); };
      if (this.mqDesktop.addEventListener) this.mqDesktop.addEventListener('change', this.onMqChange);
      else if (this.mqDesktop.addListener) this.mqDesktop.addListener(this.onMqChange);
    }

    debounce(fn, wait) {
      if (window.AirX && window.AirX.debounce) return window.AirX.debounce(fn, wait);
      var t;
      return function () {
        clearTimeout(t);
        t = setTimeout(fn, wait);
      };
    }

    joinRoot(path) {
      return this.root + path;
    }

    isMobile() {
      return !this.mqDesktop.matches;
    }

    routeFromHash() {
      var m = window.location.hash.match(/^#c\/(.+)$/);
      if (!m) return null;
      try {
        return decodeURIComponent(m[1]).toLowerCase();
      } catch (err) {
        return m[1].toLowerCase();
      }
    }

    route(cold) {
      var handle = this.routeFromHash();
      if (handle) this.openExplorer(handle, cold === true);
      else this.showConstellations();
    }

    exitExplorer() {
      if (this.routeFromHash()) window.location.hash = '';
      else this.showConstellations();
    }

    loadCategories() {
      var self = this;
      if (this.catsAbort) this.catsAbort.abort();
      this.catsAbort = new AbortController();
      var signal = this.catsAbort.signal;

      this.catsErrorEl.hidden = true;
      if (this.gridSkeleton) this.gridSkeleton.hidden = false;

      var all = [];
      var page = 1;
      var prevFirstId = null;

      function fetchPage() {
        return fetch(self.joinRoot('collections.json?limit=' + PAGE_SIZE + '&page=' + page), { signal })
          .then(function (res) {
            if (!res.ok) throw new Error(res.statusText || 'HTTP ' + res.status);
            return res.json();
          })
          .then(function (data) {
            var batch = (data && data.collections) || [];

            if (batch.length && batch[0].id === prevFirstId) return all;
            if (batch.length) prevFirstId = batch[0].id;
            all = all.concat(batch);
            if (batch.length === PAGE_SIZE) {
              page++;
              return fetchPage();
            }
            return all;
          });
      }

      fetchPage()
        .then(function (raw) {
          self.state.categories = self.prepareCategories(raw);
          self.catsLoaded = true;
          self.renderConstellations();
          self.updateStats();
          self.reconcileExplorerMeta();
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (self.gridSkeleton) self.gridSkeleton.hidden = true;
          self.catsErrorEl.hidden = false;
        });
    }

    prepareCategories(raw) {
      var self = this;
      var cats = [];
      (raw || []).forEach(function (c) {
        if (!c || !c.handle) return;
        if (self.excluded[c.handle.toLowerCase()]) return;

        var count = typeof c.products_count === 'number' ? c.products_count : null;
        if (count === 0) return;
        cats.push({ handle: c.handle, title: c.title || prettifyHandle(c.handle), count: count });
      });
      cats.sort(function (a, b) {
        if (self.sortMode === 'alpha') return a.title.localeCompare(b.title);
        var ca = a.count == null ? -1 : a.count;
        var cb = b.count == null ? -1 : b.count;
        if (cb !== ca) return cb - ca;
        return a.title.localeCompare(b.title);
      });
      return cats;
    }

    renderConstellations() {
      var self = this;
      if (this.gridSkeleton) this.gridSkeleton.hidden = true;

      Array.from(this.gridEl.children).forEach(function (child) {
        if (!child.hasAttribute('data-atlas-grid-skeleton')) child.remove();
      });

      var animate = motionOn() && window.AirX && window.AirX.motion;
      if (animate) {
        this.gridEl.setAttribute('data-animate-group', '');
        this.gridEl.removeAttribute('data-motion-init');
      }

      this.state.categories.forEach(function (cat) {
        var card = self.buildCard(cat);
        if (animate) card.setAttribute('data-animate', 'rise');
        self.gridEl.appendChild(card);
        self.cardIO.observe(card);
      });

      if (animate && window.AirX.emit) {
        window.AirX.emit('motion:scan', { root: this.constellationsEl });
      }
    }

    worksLabel(count) {
      if (count == null) return this.strings.countPending || '…';
      var tpl = count === 1 ? this.strings.worksOne : this.strings.worksOther;
      return interp(tpl, { count: count.toLocaleString() });
    }

    buildCard(cat) {
      var self = this;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'atlas-card';
      btn.dataset.handle = cat.handle;
      btn.setAttribute('aria-label', interp(this.strings.openCategory, { title: cat.title, count: this.worksLabel(cat.count) }));

      var rng = mulberry32(hashString(cat.handle));
      var base = rng() * Math.PI * 2;
      cat.orbits = [];
      var orbitsWrap = document.createElement('span');
      orbitsWrap.className = 'atlas-card__orbits';
      orbitsWrap.setAttribute('aria-hidden', 'true');

      for (var i = 0; i < 6; i++) {
        var angle = base + i * GOLDEN_ANGLE + (rng() - 0.5) * 0.6;
        var radius = 38 + rng() * 8;
        var size = Math.round(44 + rng() * 32);
        var x = Math.min(90, Math.max(10, 50 + Math.cos(angle) * radius));
        var y = Math.min(86, Math.max(14, 50 + Math.sin(angle) * radius * 0.92));

        var slot = document.createElement('span');
        slot.className = 'atlas-card__orbit skeleton';
        slot.style.left = x.toFixed(1) + '%';
        slot.style.top = y.toFixed(1) + '%';
        slot.style.width = size + 'px';
        slot.style.height = size + 'px';

        var drift = document.createElement('span');
        drift.className = 'atlas-card__orbit-drift';
        var img = document.createElement('img');
        img.alt = '';
        img.decoding = 'async';
        drift.appendChild(img);
        slot.appendChild(drift);
        orbitsWrap.appendChild(slot);

        cat.orbits.push({
          slot: slot,
          drift: drift,
          img: img,
          x: x,
          y: y,
          dur: 8 + rng() * 6,
          dx: rng() * 8 - 4,
          dy: rng() * 8 - 4
        });
      }
      btn.appendChild(orbitsWrap);

      var core = document.createElement('span');
      core.className = 'atlas-card__core';
      var title = document.createElement('span');
      title.className = 'atlas-card__title';
      title.textContent = cat.title;
      var chip = document.createElement('span');
      chip.className = 'chip atlas-card__count';
      chip.textContent = this.worksLabel(cat.count);
      core.appendChild(title);
      core.appendChild(chip);
      btn.appendChild(core);

      cat.el = btn;
      cat.countChip = chip;

      btn.addEventListener('click', function () {
        self.lastFocusedCardHandle = cat.handle;
        window.location.hash = 'c/' + encodeURIComponent(cat.handle);
      });
      btn.addEventListener('pointerenter', function () { self.onCardEnter(cat); });
      btn.addEventListener('pointerleave', function () { self.onCardLeave(cat); });

      return btn;
    }

    buildCardObserver() {
      var self = this;
      this.cardIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          self.cardIO.unobserve(entry.target);
          var cat = self.findCategory(entry.target.dataset.handle);
          if (cat) self.loadThumbs(cat);
        });
      }, { rootMargin: '320px 0px' });
    }

    findCategory(handle) {
      for (var i = 0; i < this.state.categories.length; i++) {
        if (this.state.categories[i].handle === handle) return this.state.categories[i];
      }
      return null;
    }

    loadThumbs(cat) {
      var self = this;
      if (cat.thumbsLoaded) return;
      cat.thumbsLoaded = true;

      fetch(this.joinRoot('collections/' + encodeURIComponent(cat.handle) + '/products.json?limit=' + THUMB_PROBE))
        .then(function (res) {
          if (!res.ok) throw new Error(res.statusText || 'HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var raw = (data && data.products) || [];
          var filtered = filterProducts(raw);

          if (cat.count == null && raw.length < THUMB_PROBE) {
            cat.count = filtered.length;
            if (cat.countChip) cat.countChip.textContent = self.worksLabel(cat.count);
            if (cat.count === 0) {
              self.removeCategory(cat);
              return;
            }
            self.updateStats();
          }

          var srcs = filtered.slice(0, 6).map(function (p) {
            return sizedImage(p.images[0].src, 180);
          });
          cat.orbits.forEach(function (orbit, i) {
            if (i < srcs.length) {
              orbit.img.onload = function () {
                orbit.slot.classList.remove('skeleton');
                orbit.slot.classList.add('is-loaded');
              };
              orbit.img.onerror = function () {
                orbit.slot.classList.remove('skeleton');
                orbit.slot.hidden = true;
              };
              orbit.img.src = srcs[i];
            } else {
              orbit.slot.classList.remove('skeleton');
              orbit.slot.hidden = true;
            }
          });
          self.startDrift(cat);
        })
        .catch(function () {

          cat.orbits.forEach(function (orbit) {
            orbit.slot.classList.remove('skeleton');
            orbit.slot.hidden = true;
          });
        });
    }

    removeCategory(cat) {
      this.state.categories = this.state.categories.filter(function (c) { return c !== cat; });
      if (cat.el) {
        if (motionOn()) {
          window.gsap.to(cat.el, {
            opacity: 0,
            scale: 0.96,
            duration: 0.3,
            ease: 'power2.in',
            onComplete: function () { cat.el.remove(); }
          });
        } else {
          cat.el.remove();
        }
      }
      this.updateStats();
    }

    startDrift(cat) {
      if (!motionOn() || cat.driftStarted) return;
      cat.driftStarted = true;
      cat.orbits.forEach(function (orbit, i) {
        if (orbit.slot.hidden) return;
        window.gsap.to(orbit.drift, {
          x: orbit.dx,
          y: orbit.dy,
          duration: orbit.dur / 2,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          delay: i * 0.35
        });
      });
    }

    onCardEnter(cat) {
      if (!motionOn() || !cat.el) return;
      var w = cat.el.offsetWidth || 300;
      cat.orbits.forEach(function (orbit) {
        if (orbit.slot.hidden) return;
        var dx = (orbit.x - 50) / 50;
        var dy = (orbit.y - 50) / 50;
        window.gsap.to(orbit.slot, {
          x: dx * w * 0.06,
          y: dy * w * 0.06,
          scale: 1.06,
          duration: 0.5,
          ease: 'power3.out',
          overwrite: 'auto'
        });
      });
    }

    onCardLeave(cat) {
      if (!motionOn() || !cat.el) return;
      cat.orbits.forEach(function (orbit) {
        if (orbit.slot.hidden) return;
        window.gsap.to(orbit.slot, { x: 0, y: 0, scale: 1, duration: 0.6, ease: 'power3.out', overwrite: 'auto' });
      });
    }

    updateStats() {
      if (!this.showStats || !this.statsEl || !this.state.categories.length) return;
      var moods = this.state.categories.length;
      var total = 0;
      this.state.categories.forEach(function (c) { total += c.count || 0; });

      var moodsTpl = moods === 1 ? this.strings.moodsOne : this.strings.moodsOther;
      var moodsNum = this.buildStatPart(this.statMoodsEl, moodsTpl, moods);
      var artNum = null;
      if (total > 0) {
        artNum = this.buildStatPart(this.statArtworksEl, this.strings.artworksStat, total);
        this.statArtworksEl.hidden = false;
      } else {
        this.statArtworksEl.hidden = true;
      }

      this.statsEl.hidden = false;
      this.statsEl.classList.add('is-ready');

      if (!this.statsBuilt && motionOn() && window.AirX && window.AirX.motion && window.AirX.emit) {
        if (moodsNum) moodsNum.setAttribute('data-counter', String(moods));
        if (artNum) artNum.setAttribute('data-counter', String(total));
        window.AirX.emit('motion:scan', { root: this.statsEl });
      }
      this.statsBuilt = true;
    }

    buildStatPart(el, template, value) {
      if (!el) return null;
      el.textContent = '';
      var parts = String(template == null ? '[count]' : template).split('[count]');
      if (parts[0]) el.appendChild(document.createTextNode(parts[0]));
      var num = document.createElement('span');
      num.className = 'atlas__stat-num';
      num.textContent = value.toLocaleString();
      el.appendChild(num);
      if (parts[1]) el.appendChild(document.createTextNode(parts[1]));
      return num;
    }

    showConstellations() {
      if (this.state.view === 'constellations') return;
      var self = this;
      this.state.view = 'constellations';
      this.state.activeCategory = null;
      this.runToken++;
      if (this.wallAbort) this.wallAbort.abort();
      if (this.sheetOpen) this.collapseSheet(true);
      this.peekEl.hidden = true;

      var finish = function () {
        self.explorerEl.hidden = true;
        self.constellationsEl.hidden = false;
        window.scrollTo({ top: self.savedScroll, behavior: 'auto' });
        if (motionOn()) {
          window.gsap.fromTo(
            self.constellationsEl,
            { opacity: 0, scale: 0.985 },
            { opacity: 1, scale: 1, duration: 0.4, ease: 'power3.out', clearProps: 'opacity,transform' }
          );
        }

        var cat = self.lastFocusedCardHandle && self.findCategory(self.lastFocusedCardHandle);
        if (cat && cat.el) cat.el.focus({ preventScroll: true });
      };

      if (motionOn()) {
        window.gsap.to(this.explorerEl, {
          opacity: 0,
          y: 20,
          duration: 0.3,
          ease: 'power2.in',
          onComplete: function () {
            window.gsap.set(self.explorerEl, { clearProps: 'opacity,transform' });
            finish();
          }
        });
      } else {
        finish();
      }
    }

    openExplorer(handle, cold) {
      var self = this;
      if (this.state.view === 'explorer' && this.state.activeCategory === handle) return;

      var fromConstellations = this.state.view !== 'explorer';
      if (fromConstellations && !cold) this.savedScroll = window.scrollY;

      this.state.view = 'explorer';
      this.state.activeCategory = handle;
      this.lastFocusedCardHandle = handle;

      this.resetExplorerUI();
      this.setExplorerCategory(this.findCategory(handle), handle);

      var reveal = function () {
        self.constellationsEl.hidden = true;
        self.explorerEl.hidden = false;
        self.scrollToTop(cold);
        if (motionOn() && !cold) {
          window.gsap.fromTo(
            self.explorerEl,
            { opacity: 0, y: 28 },
            { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out', clearProps: 'opacity,transform' }
          );
        }
        var back = self.explorerEl.querySelector('[data-atlas-back]');
        if (back && !cold) back.focus({ preventScroll: true });
      };

      if (fromConstellations && motionOn() && !cold) {
        window.gsap.to(this.constellationsEl, {
          opacity: 0,
          scale: 0.985,
          duration: 0.35,
          ease: 'power3.inOut',
          onComplete: function () {
            window.gsap.set(self.constellationsEl, { clearProps: 'opacity,transform' });
            reveal();
          }
        });
      } else {
        reveal();
      }

      this.startProducts(handle);
    }

    scrollToTop(cold) {
      if (cold) return;
      var top = this.getBoundingClientRect().top + window.scrollY - 80;
      if (window.scrollY > top) {
        window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
      }
    }

    resetExplorerUI() {
      this.runToken++;
      if (this.wallAbort) this.wallAbort.abort();

      Array.from(this.wallEl.children).forEach(function (child) {
        if (!child.hasAttribute('data-atlas-wall-skeleton')) child.remove();
      });
      if (this.wallSkeleton) this.wallSkeleton.hidden = false;
      this.items = [];
      this.tiles = [];
      this.imageQueue = [];
      this.buildImageObserver();
      this._hoverTile = null;
      clearTimeout(this._hoverTimer);

      this.filterQuery = '';
      if (this.filterInput) this.filterInput.value = '';
      this.querySelectorAll('[data-atlas-filter-clear]').forEach(function (b) { b.hidden = true; });
      this.filterEmptyEl.hidden = true;
      this.wallErrorEl.hidden = true;
      this.wallEmptyEl.hidden = true;

      this.progressEl.hidden = false;
      this.progressEl.classList.add('is-indeterminate');
      this.progressBar.style.width = '';
      this.statusEl.textContent = this.strings.loadingWall || '';
      if (this.liveEl) this.liveEl.textContent = '';

      this.state.selection = -1;
      this.currentVariant = null;
      this.lastCents = null;
      this.stageToken++;
      this.previewContent.hidden = true;
      this.previewEmpty.hidden = false;
      this.previewError.hidden = true;
      this.peekEl.hidden = true;
      if (this.sheetOpen) this.collapseSheet(true);
      if (this.frontImg) { this.frontImg.classList.remove('is-front'); this.frontImg.removeAttribute('src'); }
      if (this.backImg) { this.backImg.classList.remove('is-front'); this.backImg.removeAttribute('src'); }
    }

    setExplorerCategory(cat, handle) {
      var title = cat ? cat.title : null;
      if (title) {
        this.titleEl.textContent = title;
      } else if (this.catsLoaded) {

        this.titleEl.textContent = prettifyHandle(handle);
      } else {

        this.titleEl.textContent = '';
        if (this.titleGhost) {
          this.titleEl.appendChild(this.titleGhost);
          this.titleGhost.hidden = false;
        }
      }
      this.countEl.textContent = this.worksLabel(cat ? cat.count : null);
      if (this.linkEl) this.linkEl.href = this.joinRoot('collections/' + encodeURIComponent(handle));
    }

    reconcileExplorerMeta() {
      if (this.state.view !== 'explorer' || !this.state.activeCategory) return;
      this.setExplorerCategory(this.findCategory(this.state.activeCategory), this.state.activeCategory);
    }

    startProducts(handle) {
      var self = this;

      if (this.wallAbort) this.wallAbort.abort();
      var entry = this.state.products.get(handle);
      if (!entry) {
        entry = { items: [], page: 0, done: false, error: false };
        this.state.products.set(handle, entry);
      }
      entry.error = false;
      this.wallErrorEl.hidden = true;

      if (entry.items.length && this.items.length === 0) this.appendItems(entry.items, 0, true);
      if (entry.done) {
        this.finishWall(entry);
        return;
      }

      var token = ++this.runToken;
      this.wallAbort = new AbortController();
      var signal = this.wallAbort.signal;

      function nextPage() {
        var page = entry.page + 1;
        return fetch(
          self.joinRoot('collections/' + encodeURIComponent(handle) + '/products.json?limit=' + PAGE_SIZE + '&page=' + page),
          { signal }
        )
          .then(function (res) {
            if (!res.ok) throw new Error(res.statusText || 'HTTP ' + res.status);
            return res.json();
          })
          .then(function (data) {
            if (token !== self.runToken) return;
            var raw = (data && data.products) || [];
            entry.page = page;
            var fresh = filterProducts(raw);
            var start = entry.items.length;
            entry.items = entry.items.concat(fresh);
            if (raw.length < PAGE_SIZE) entry.done = true;

            if (fresh.length) self.appendItems(fresh, start, false);
            self.updateWallStatus(entry);

            if (entry.done) {
              self.finishWall(entry);
              return;
            }
            return nextPage();
          });
      }

      this.updateWallStatus(entry);
      nextPage().catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (token !== self.runToken) return;
        entry.error = true;
        self.wallSkeleton.hidden = true;
        self.progressEl.hidden = true;
        self.statusEl.textContent = '';
        self.wallErrorEl.hidden = false;
      });
    }

    finishWall(entry) {
      var handle = this.state.activeCategory;
      this.progressEl.hidden = true;
      this.progressEl.classList.remove('is-indeterminate');
      this.wallSkeleton.hidden = true;

      var cat = this.findCategory(handle);
      if (cat && cat.count !== entry.items.length) {
        cat.count = entry.items.length;
        if (cat.countChip) cat.countChip.textContent = this.worksLabel(cat.count);
        if (cat.count === 0) this.removeCategory(cat);
        else this.updateStats();
      }
      this.reconcileExplorerMeta();
      this.updateWallStatus(entry);

      if (entry.items.length === 0) {
        this.wallEmptyEl.hidden = false;
        this.statusEl.textContent = '';
      }
    }

    appendItems(newItems, startIndex, fromCache) {
      var self = this;
      if (this.wallSkeleton) this.wallSkeleton.hidden = true;

      var frag = document.createDocumentFragment();
      var batch = null;
      var appended = [];

      newItems.forEach(function (item, i) {
        var index = startIndex + i;
        if (index % BATCH_SIZE === 0 || !batch) {
          batch = document.createElement('div');
          batch.className = 'atlas-wall__batch';
          frag.appendChild(batch);
        }
        item.titleLower = (item.title || '').toLowerCase();
        var tile = self.buildTile(item, index);
        self.tiles[index] = tile;
        self.items[index] = item;
        batch.appendChild(tile);
        appended.push(tile);
      });

      var lastBatch = this.wallEl.querySelector('.atlas-wall__batch:not([data-atlas-wall-skeleton]):last-of-type');
      if (lastBatch && lastBatch.children.length % BATCH_SIZE !== 0 && frag.firstChild) {
        var firstNew = frag.firstChild;
        while (firstNew.firstChild && lastBatch.children.length < BATCH_SIZE) {
          lastBatch.appendChild(firstNew.firstChild);
        }
        if (!firstNew.firstChild) frag.removeChild(firstNew);
      }
      this.wallEl.appendChild(frag);

      if (this.filterQuery) this.applyFilter(true);
      else this.syncRovingTabindex();

      if (!fromCache && motionOn() && appended.length) {
        window.gsap.fromTo(
          appended.slice(0, 160),
          { opacity: 0, y: 10 },
          { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', stagger: 0.006, clearProps: 'opacity,transform', overwrite: true }
        );
      }

      if (this.isMobile() && this.state.selection < 0 && this.items.length) {
        this.selectTile(0, {});
      }
    }

    buildTile(item, index) {
      var tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'atlas-tile';
      tile.dataset.index = String(index);
      tile.tabIndex = -1;
      tile.setAttribute('aria-label', item.title || '');

      var img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      img.loading = 'lazy';
      img.dataset.src = sizedImage(item.images[0].src, 240);
      tile.appendChild(img);

      this.tileIO.observe(tile);
      return tile;
    }

    buildImageObserver() {
      var self = this;
      if (this.tileIO) this.tileIO.disconnect();
      this.tileIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          self.tileIO.unobserve(entry.target);
          self.queueTileImage(entry.target);
        });
      }, { rootMargin: '600px 0px' });
    }

    queueTileImage(tile) {
      if (this.loadingImages >= MAX_IMG_LOADS) {
        this.imageQueue.push(tile);
        return;
      }
      this.startTileImage(tile);
    }

    startTileImage(tile) {
      var self = this;
      var img = tile.firstElementChild;
      if (!img || img.src || !img.dataset.src) return;
      this.loadingImages++;
      var settle = function (ok) {
        self.loadingImages--;
        tile.classList.add(ok ? 'is-loaded' : 'is-error');
        var next;
        while ((next = self.imageQueue.shift())) {
          if (next.isConnected) {
            self.startTileImage(next);
            break;
          }
        }
      };
      img.onload = function () { settle(true); };
      img.onerror = function () { settle(false); };
      img.src = img.dataset.src;
    }

    updateWallStatus(entry) {
      var loaded = entry.items.length;
      var cat = this.findCategory(this.state.activeCategory);
      var total = entry.done ? loaded : (cat && cat.count != null ? cat.count : null);

      if (this.filterQuery) return;

      if (!entry.done) {
        this.statusEl.textContent = loaded
          ? interp(this.strings.showingOf, {
              x: loaded.toLocaleString(),
              y: total != null ? total.toLocaleString() : (this.strings.countPending || '…')
            })
          : (this.strings.loadingWall || '');
        this.progressEl.hidden = false;
        if (total) {
          this.progressEl.classList.remove('is-indeterminate');
          var pct = Math.max(4, Math.min(100, Math.round((loaded / total) * 100)));
          this.progressBar.style.width = pct + '%';
          this.progressEl.setAttribute('aria-valuenow', String(pct));
        } else {
          this.progressEl.classList.add('is-indeterminate');
        }
      } else {
        this.progressEl.hidden = true;
        this.statusEl.textContent = loaded ? this.worksLabel(loaded) : '';
      }
    }

    applyFilter(silent) {
      var q = this.filterInput ? this.filterInput.value.trim().toLowerCase() : '';
      this.filterQuery = q;
      this.querySelectorAll('[data-atlas-filter-clear]').forEach(function (b) { b.hidden = !q; });

      var visible = 0;
      for (var i = 0; i < this.tiles.length; i++) {
        var tile = this.tiles[i];
        if (!tile) continue;
        var match = !q || this.items[i].titleLower.indexOf(q) !== -1;
        tile.hidden = !match;
        if (match) visible++;
      }

      var entry = this.state.products.get(this.state.activeCategory);
      var loaded = entry ? entry.items.length : 0;

      if (q) {
        this.statusEl.textContent = interp(this.strings.showingOf, {
          x: visible.toLocaleString(),
          y: loaded.toLocaleString()
        });
        this.filterEmptyEl.hidden = visible !== 0;
        if (this.filterEmptyTitle && !visible) {
          this.filterEmptyTitle.textContent = interp(this.strings.filterEmptyTitle, { query: this.filterInput.value.trim() });
        }
        if (this.liveEl && !silent) this.liveEl.textContent = this.statusEl.textContent;
      } else {
        this.filterEmptyEl.hidden = true;
        if (entry) this.updateWallStatus(entry);
        if (this.liveEl && !silent) this.liveEl.textContent = this.statusEl.textContent;
      }
      this.syncRovingTabindex();
    }

    clearFilter(refocus) {
      if (!this.filterInput) return;
      this.filterInput.value = '';
      this.applyFilter();
      if (refocus) this.filterInput.focus({ preventScroll: true });
    }

    visibleTiles() {
      return this.tiles.filter(function (t) { return t && !t.hidden; });
    }

    syncRovingTabindex() {
      var visible = this.visibleTiles();
      if (!visible.length) return;
      var selected = this.tiles[this.state.selection];
      var anchor = selected && !selected.hidden ? selected : visible[0];
      visible.forEach(function (t) { t.tabIndex = t === anchor ? 0 : -1; });
    }

    columnCount() {
      var batch = this.wallEl.querySelector('.atlas-wall__batch:not([data-atlas-wall-skeleton])');
      if (!batch) return 1;
      var cols = getComputedStyle(batch).gridTemplateColumns.split(' ').length;
      return Math.max(1, cols);
    }

    onWallKeydown(e) {
      var keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (keys.indexOf(e.key) === -1) return;
      var visible = this.visibleTiles();
      if (!visible.length) return;
      e.preventDefault();

      var current = this.tiles[this.state.selection];
      var pos = current ? visible.indexOf(current) : -1;
      if (pos === -1) pos = 0;
      var cols = this.columnCount();

      switch (e.key) {
        case 'ArrowLeft': pos = Math.max(0, pos - 1); break;
        case 'ArrowRight': pos = Math.min(visible.length - 1, pos + 1); break;
        case 'ArrowUp': pos = Math.max(0, pos - cols); break;
        case 'ArrowDown': pos = Math.min(visible.length - 1, pos + cols); break;
        case 'Home': pos = 0; break;
        case 'End': pos = visible.length - 1; break;
      }
      this.selectTile(parseInt(visible[pos].dataset.index, 10), { focus: true });
    }

    selectTile(index, opts) {
      var item = this.items[index];
      var tile = this.tiles[index];
      if (!item || !tile) return;

      if (this.state.selection !== index) {
        var prev = this.tiles[this.state.selection];
        if (prev) {
          prev.classList.remove('is-selected');
          prev.tabIndex = -1;
        }
        tile.classList.add('is-selected');
        this.state.selection = index;
        this.syncRovingTabindex();
        this.updatePreview(item);
        this.preloadAround(index);
      }
      if (opts && opts.focus) tile.focus({ preventScroll: false });
    }

    updatePreview(item) {

      if (this.previewContent.hidden) {
        this.previewEmpty.hidden = true;
        this.previewContent.hidden = false;
        if (motionOn()) {
          window.gsap.fromTo(
            this.previewContent,
            { opacity: 0, y: 14 },
            { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out', clearProps: 'opacity,transform' }
          );
        }
      }

      this.previewError.hidden = true;
      this.showArtwork(item);
      this.previewTitle.textContent = item.title || '';

      var text = stripHtmlExcerpt(item.body_html, 40);
      this.excerptEl.textContent = text;
      this.excerptEl.hidden = !text;

      var variants = item.variants || [];
      var sizes = [];
      var depths = [];
      variants.forEach(function (v) {
        if (v.option1 && sizes.indexOf(v.option1) === -1) sizes.push(v.option1);
        if (v.option2 && depths.indexOf(v.option2) === -1) depths.push(v.option2);
      });
      item._sizes = sizes;
      item._depths = depths;

      if (!this.currentDepth || depths.indexOf(this.currentDepth) === -1) {
        this.currentDepth = depths[0] || null;
      }

      this.selectedSize = null;
      for (var i = 0; i < sizes.length; i++) {
        var v = this.variantFor(item, sizes[i], this.currentDepth);
        if (v && v.available) {
          this.selectedSize = sizes[i];
          break;
        }
      }
      if (!this.selectedSize) this.selectedSize = sizes[0] || null;

      this.renderDepths(item);
      this.renderSizes(item);
      this.syncBuyState(item);

      if (this.detailsLink) this.detailsLink.href = this.joinRoot('products/' + encodeURIComponent(item.handle));

      this.updatePeek(item);
    }

    variantFor(item, size, depth) {
      var variants = item.variants || [];
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (size != null && v.option1 !== size) continue;
        if (depth != null && item._depths.length > 1 && v.option2 !== depth) continue;
        return v;
      }
      return null;
    }

    depthLabel(value) {
      if (/3\s*\/\s*4/.test(value)) return this.strings.depthSlim || value;
      if (/1\s*1\s*\/\s*2/.test(value)) return this.strings.depthGallery || value;
      return value;
    }

    renderDepths(item) {
      var self = this;
      var depths = item._depths;
      this.depthsEl.textContent = '';
      if (depths.length < 2) {
        this.depthsWrap.hidden = true;
        return;
      }
      this.depthsWrap.hidden = false;
      depths.forEach(function (depth) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'atlas-depth__option';
        btn.textContent = self.depthLabel(depth);
        btn.setAttribute('aria-pressed', String(depth === self.currentDepth));
        if (depth === self.currentDepth) btn.classList.add('is-selected');
        btn.addEventListener('click', function () {
          if (self.currentDepth === depth) return;
          self.currentDepth = depth;
          var current = self.items[self.state.selection];
          if (!current) return;
          self.renderDepths(current);
          self.renderSizes(current);
          self.syncBuyState(current);
          self.updatePeek(current);
        });
        self.depthsEl.appendChild(btn);
      });
    }

    renderSizes(item) {
      var self = this;
      var sizes = item._sizes;
      this.sizesEl.textContent = '';
      var hideSizes = !sizes.length || (sizes.length === 1 && sizes[0] === 'Default Title');
      this.sizesWrap.hidden = hideSizes;
      if (hideSizes) return;

      sizes.forEach(function (size) {
        var variant = self.variantFor(item, size, self.currentDepth);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'atlas-size';
        btn.setAttribute('aria-pressed', String(size === self.selectedSize));
        if (size === self.selectedSize) btn.classList.add('is-selected');
        if (variant && !variant.available) btn.classList.add('is-soldout');
        if (!variant) btn.classList.add('is-unavailable');

        var label = document.createElement('span');
        label.className = 'atlas-size__label';
        label.textContent = prettySize(size);
        btn.appendChild(label);
        if (variant) {
          var price = document.createElement('span');
          price.className = 'atlas-size__price';
          price.textContent = formatMoney(centsFrom(variant.price));
          btn.appendChild(price);
        }

        if (variant && !variant.available) {
          var srSold = document.createElement('span');
          srSold.className = 'visually-hidden';
          srSold.textContent = ', ' + (self.strings.soldOut || 'Sold out');
          btn.appendChild(srSold);
        } else if (!variant) {
          var srUnavailable = document.createElement('span');
          srUnavailable.className = 'visually-hidden';
          srUnavailable.textContent = ', ' + (self.strings.unavailable || 'Unavailable');
          btn.appendChild(srUnavailable);
        }
        btn.addEventListener('click', function () {
          if (self.selectedSize === size) return;
          self.selectedSize = size;
          var current = self.items[self.state.selection];
          if (!current) return;
          self.renderSizes(current);
          self.syncBuyState(current);
          self.updatePeek(current);
        });
        self.sizesEl.appendChild(btn);
      });
    }

    syncBuyState(item) {

      var variant = this.variantFor(item, this.selectedSize, this.currentDepth) || (item.variants && item.variants[0]) || null;
      if (variant && this.selectedSize && variant.option1 !== this.selectedSize) variant = null;
      if (variant && this.currentDepth != null && item._depths.length > 1 && variant.option2 !== this.currentDepth) variant = null;
      this.currentVariant = variant;

      var atcOk = !!(this.currentVariant && this.currentVariant.available);
      var label = this.strings.addToCart;
      if (!this.currentVariant) label = this.strings.unavailable;
      else if (!this.currentVariant.available) label = this.strings.soldOut;

      if (this.atcLabel) this.atcLabel.textContent = label;
      this.querySelectorAll('[data-atlas-atc]').forEach(function (btn) {
        btn.setAttribute('aria-disabled', String(!atcOk));
      });

      this.setPrice(this.currentVariant ? centsFrom(this.currentVariant.price) : null);
    }

    setPrice(cents) {
      var self = this;
      if (cents == null) {
        this.priceEl.textContent = '';
        if (this.peekPrice) this.peekPrice.textContent = '';
        this.lastCents = null;
        return;
      }
      if (this._priceTween) this._priceTween.kill();
      if (motionOn() && this.lastCents != null && this.lastCents !== cents) {
        var proxy = { v: this.lastCents };
        this._priceTween = window.gsap.to(proxy, {
          v: cents,
          duration: 0.35,
          ease: 'power2.out',
          onUpdate: function () { self.priceEl.textContent = formatMoney(Math.round(proxy.v)); }
        });
      } else {
        this.priceEl.textContent = formatMoney(cents);
      }
      if (this.peekPrice) this.peekPrice.textContent = formatMoney(cents);
      this.lastCents = cents;
    }

    showArtwork(item) {
      var self = this;
      var url = sizedImage(item.images[0].src, 900);
      if (this.frontImg.getAttribute('src') === url) {

        this.stageToken++;
        return;
      }
      var token = ++this.stageToken;
      var incoming = this.backImg;
      var swap = function () {
        if (token !== self.stageToken) return;
        incoming.classList.add('is-front');
        self.frontImg.classList.remove('is-front');
        var old = self.frontImg;
        self.frontImg = incoming;
        self.backImg = old;
      };
      incoming.src = url;
      if (incoming.decode) {
        incoming.decode().then(swap).catch(function () {
          if (incoming.complete) swap();
          else incoming.onload = swap;
        });
      } else {
        incoming.onload = swap;
        if (incoming.complete) swap();
      }
    }

    preloadAround(index) {
      var visible = this.visibleTiles();
      var current = this.tiles[index];
      var pos = visible.indexOf(current);
      if (pos === -1) return;
      var candidates = [pos - 2, pos - 1, pos + 1, pos + 2];
      for (var i = 0; i < candidates.length; i++) {
        var t = visible[candidates[i]];
        if (!t) continue;
        var item = this.items[parseInt(t.dataset.index, 10)];
        if (!item) continue;
        var url = sizedImage(item.images[0].src, 900);
        if (this.preloaded[url]) continue;
        this.preloaded[url] = true;
        var img = new Image();
        img.src = url;
      }
    }

    addToCart(btn) {
      var self = this;
      if (btn.getAttribute('aria-disabled') === 'true' || btn.classList.contains('is-working')) return;
      if (!this.currentVariant || !this.currentVariant.available) return;
      if (!(window.AirX && window.AirX.cart)) return;

      var addedId = this.currentVariant.id;
      btn.classList.add('is-working');
      btn.classList.add(btn.classList.contains('btn') ? 'btn--loading' : 'is-loading');
      this.previewError.hidden = true;

      window.AirX.cart
        .add({ id: addedId, quantity: 1 })
        .then(function () {
          self.settleAtc(btn);

          if (self.sheetOpen) self.collapseSheet(true);

          if (self.atcLabel && self.currentVariant && self.currentVariant.id === addedId) {
            self.atcLabel.textContent = self.strings.added;
            clearTimeout(self._addedTimer);
            self._addedTimer = setTimeout(function () {
              if (self.currentVariant && self.currentVariant.id === addedId) {
                self.atcLabel.textContent = self.strings.addToCart;
              }
            }, 1600);
          }
        })
        .catch(function (err) {
          self.settleAtc(btn);
          var message = (err && err.message) || self.strings.cartError;

          if (self.isMobile() && !self.sheetOpen) self.expandSheet();
          requestAnimationFrame(function () {
            self.previewError.textContent = message;
            self.previewError.hidden = false;
            if (self.previewError.scrollIntoView) self.previewError.scrollIntoView({ block: 'nearest' });
          });
        });
    }

    settleAtc(btn) {
      btn.classList.remove('is-working');
      btn.classList.remove('btn--loading');
      btn.classList.remove('is-loading');
    }

    updatePeek(item) {
      if (!this.peekEl) return;
      if (this.state.view === 'explorer' && this.isMobile()) this.peekEl.hidden = false;
      if (this.peekImg) {
        this.peekImg.src = sizedImage(item.images[0].src, 180);
        this.peekImg.hidden = false;
      }
      if (this.peekTitle) this.peekTitle.textContent = item.title || '';
      if (this.peekExpand) {
        this.peekExpand.setAttribute('aria-label', (item.title || '') + ' — ' + (this.strings.expandPreview || ''));
      }

    }

    expandSheet() {
      if (this.sheetOpen || !this.isMobile()) return;
      this.sheetOpen = true;
      this.previewEl.classList.add('is-expanded');
      this.previewEl.setAttribute('role', 'dialog');
      this.previewEl.setAttribute('aria-modal', 'true');
      if (this.peekExpand) this.peekExpand.setAttribute('aria-expanded', 'true');
      if (this.previewScroll) this.previewScroll.scrollTop = 0;
      if (window.AirX) {
        if (window.AirX.overlayOpened) window.AirX.overlayOpened('atlas-sheet');
        if (window.AirX.trapFocus) window.AirX.trapFocus(this.previewEl, this.sheetClose);
      }
    }

    collapseSheet(silent) {
      if (!this.sheetOpen) return;
      this.sheetOpen = false;
      this.previewEl.classList.remove('is-expanded');
      this.previewEl.style.transform = '';
      this.previewEl.removeAttribute('role');
      this.previewEl.removeAttribute('aria-modal');
      if (this.peekExpand) this.peekExpand.setAttribute('aria-expanded', 'false');
      if (window.AirX) {
        if (window.AirX.overlayClosed) window.AirX.overlayClosed('atlas-sheet');
        if (window.AirX.releaseFocus) window.AirX.releaseFocus(this.previewEl);
      }
      if (!silent && this.peekExpand && !this.peekEl.hidden) this.peekExpand.focus({ preventScroll: true });
    }

    bindSheetDrag() {
      var self = this;
      var startY = 0;
      var dragging = false;

      this.sheetHandle.addEventListener('pointerdown', function (e) {
        if (!self.sheetOpen) return;
        dragging = true;
        startY = e.clientY;
        self.previewEl.classList.add('is-dragging');
        try { self.sheetHandle.setPointerCapture(e.pointerId); } catch (err) {}
      });
      this.sheetHandle.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dy = Math.max(0, e.clientY - startY);
        self.previewEl.style.transform = 'translateY(' + dy + 'px)';
      });
      var release = function (e) {
        if (!dragging) return;
        dragging = false;
        self.previewEl.classList.remove('is-dragging');
        var dy = Math.max(0, e.clientY - startY);
        self.previewEl.style.transform = '';
        if (dy > 90) self.collapseSheet();
      };
      this.sheetHandle.addEventListener('pointerup', release);
      this.sheetHandle.addEventListener('pointercancel', release);
    }

    syncViewport() {
      if (this.mqDesktop.matches) {

        if (this.sheetOpen) this.collapseSheet(true);
        this.peekEl.hidden = true;
      } else if (this.state.view === 'explorer') {
        if (this.state.selection >= 0) {
          this.peekEl.hidden = false;
        } else if (this.items.length) {
          this.selectTile(0, {});
        }
      }
    }
  }

  customElements.define('artwork-atlas', ArtworkAtlas);
})();
